/** Same exclusions as `speculationRulesPayload` (quicklink fallback path). */
import { perfLog, perfTime } from './perf'

const quicklinkIgnores = [
  /wp-admin/,
  /wp-content\/uploads/,
  /\?.+/,
  (uri, elem) =>
    elem.relList?.contains?.('nofollow') ??
    String(elem.getAttribute('rel') || '')
      .split(/\s+/)
      .includes('nofollow'),
  (uri, elem) => Boolean(elem.closest('.no-prefetch')),
  (uri, elem) => elem.hasAttribute('data-skip-prefetch'),
  (uri, elem) => {
    try {
      return new URL(elem.href).origin !== window.location.origin
    } catch {
      return true
    }
  },
  (uri) => /\.(pdf|zip|docx?|xlsx?|jpe?g|png|gif)(\?|$)/i.test(uri)
]

const speculationRulesPayload = {
  prefetch: [
    {
      source: 'document',
      where: {
        and: [
          { href_matches: '/*' },
          { not: { href_matches: '/wp-*.php' } },
          { not: { href_matches: '/wp-admin/*' } },
          { not: { href_matches: '/wp-content/uploads/*' } },
          { not: { href_matches: '/*\\?(.+)' } },
          { not: { selector_matches: 'a[rel~="nofollow"]' } },
          { not: { selector_matches: '.no-prefetch, .no-prefetch a' } },
          { not: { selector_matches: '[data-skip-prefetch]' } }
        ]
      },
      eagerness: 'moderate'
    }
  ]
}

let nativeSpecRulesActive = false
/** @type {(() => void) | null} */
let quicklinkCleanup = null

const injectSpeculationRules = () => {
  const script = document.createElement('script')
  script.type = 'speculationrules'
  script.textContent = JSON.stringify(speculationRulesPayload)
  document.head.appendChild(script)
}

const startQuicklink = async () => {
  const { listen } = await import('quicklink')
  if (quicklinkCleanup) {
    quicklinkCleanup()
    quicklinkCleanup = null
  }
  quicklinkCleanup = listen({ ignores: quicklinkIgnores })
}

/**
 * Initialize prefetch: native speculation rules when supported, otherwise lazy-load quicklink.
 */
export const initPrefetch = () => {
  void (async () => {
    try {
      if (HTMLScriptElement.supports?.('speculationrules')) {
        injectSpeculationRules()
        nativeSpecRulesActive = true
        return
      }
      await startQuicklink()
    } catch (error) {
      console.debug('Prefetch init failed:', error)
    }
  })()
}

/**
 * Re-run quicklink after DOM swaps (e.g. Highway). No-op when native speculation rules are active.
 */
export const refreshPrefetch = () => {
  if (nativeSpecRulesActive) return
  void (async () => {
    try {
      await startQuicklink()
    } catch (error) {
      console.debug('Prefetch refresh failed:', error)
    }
  })()
}

/**
 * Disconnect quicklink observers (fallback path only).
 */
export const destroyPrefetch = () => {
  if (quicklinkCleanup) {
    quicklinkCleanup()
    quicklinkCleanup = null
  }
}

const prefetchedUrls = new Set()
const pendingPrefetches = new Set()
/** @type {Map<string, number>} url → prefetch duration ms */
const prefetchDurations = new Map()

const normalizeUrl = (url) => {
  try {
    return new URL(url, window.location.href).href
  } catch {
    return null
  }
}

export const isHighwayCached = (url) => {
  const href = normalizeUrl(url)
  if (!href) return false
  return window.H?.cache?.has(href) ?? false
}

export const wasPrefetched = (url) => {
  const href = normalizeUrl(url)
  if (!href) return false
  return prefetchedUrls.has(href) || isHighwayCached(href)
}

export const getPrefetchDuration = (url) => {
  const href = normalizeUrl(url)
  if (!href) return null
  return prefetchDurations.get(href) ?? null
}

/** Same fetch options as Highway Core.fetch() */
const fetchForHighway = async (href) => {
  const response = await fetch(href, {
    mode: 'same-origin',
    method: 'GET',
    headers: { 'X-Requested-With': 'Highway' },
    credentials: 'same-origin'
  })

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`HTTP ${response.status}`)
  }

  return response.text()
}

const warmHighwayCache = (href, html) => {
  if (!window.H?.Helpers || !window.H?.cache) return false
  window.H.cache.set(href, window.H.Helpers.getProperties(html))
  return true
}

const shouldPrefetchUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.href)
    if (parsed.origin !== window.location.origin) return false

    const href = parsed.href
    if (/wp-admin/.test(href)) return false
    if (/wp-content\/uploads/.test(href)) return false
    if (/\?.+/.test(href)) return false
    if (/\.(pdf|zip|docx?|xlsx?|jpe?g|png|gif)(\?|$)/i.test(href)) return false

    return true
  } catch {
    return false
  }
}

/** Prefetch a URL on demand (e.g. map marker hover) into Highway's in-memory cache. */
export const prefetchUrl = (url) => {
  const href = normalizeUrl(url)
  if (!href || !shouldPrefetchUrl(href)) return

  if (isHighwayCached(href)) {
    perfLog('↗ prefetch skip (Highway cache hit)', { url: href })
    return
  }

  if (prefetchedUrls.has(href) || pendingPrefetches.has(href)) {
    perfLog('↗ prefetch skip (already queued/done)', { url: href })
    return
  }

  pendingPrefetches.add(href)
  perfLog('↗ prefetch start', { url: href })

  void (async () => {
    const end = perfTime('prefetch')
    try {
      const html = await fetchForHighway(href)
      const cached = warmHighwayCache(href, html)
      prefetchedUrls.add(href)
      const ms = Number(end({ url: href, status: 'done', highwayCache: cached }))
      prefetchDurations.set(href, ms)
      if (cached) {
        perfLog('↗ prefetch warmed Highway cache', { url: href })
      }
    } catch (error) {
      end({ url: href, status: 'failed', error: String(error) })
    } finally {
      pendingPrefetches.delete(href)
    }
  })()
}